import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { IconButton, Collapse } from '@mui/material';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from "@mui/icons-material/Close";

import Button from '@mui/material/Button';
import CustomTable from '../../components/CustomTable';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import AddModal from '../../components/AddModal';
import UploadModal from '../../components/UploadModal';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL } from '../../common/AppConstants';

const TITLE = 'Subjects';
const API_URL = 'academics/subject/';
const SUB_SUBJECT_API_URL = 'academics/subject-component/'; // Constant for the Sub Subject API endpoint
const UPLOAD_TEMPLATE = 'subject_upload_template.xlsx';
const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';

const subjectComponentColumns = [
  {
    attr: 'subject_component_type',
    editable: true,
    extra: false,
    label: 'Session type',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Session Type',
  },
  {
    attr: 'slots_per_session',
    editable: true,
    extra: false,
    label: 'Slot size',
    related: false,
    required: true,
    type: 'text',
    help_text: 'How many time-slots/hours are required per session',
  },
  {
    attr: 'sessions_per_cycle',
    editable: true,
    extra: false,
    label: 'Weekly slots',
    related: false,
    required: true,
    type: 'text',
    help_text: 'How many times in a week this needs to be scheduled',
  },
  {
    attr: 'classroom_type',
    editable: true,
    extra: false,
    label: 'Classroom',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Classroom Type',
  },
  {
    attr: 'edit',
    editable: true,
    extra: true,
    label: 'Edit',
    related: false,
    required: true,
    type: 'text',
  },
];

function preventDefault(event) {
  event.preventDefault();
}

export default function Subjects() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    row_item.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleEditClick = (row) => {
    console.log('Edit row ', row);
    setSelectedEditRow(row);
  };

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]); // Runs when selectedEditRow changes

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row, index) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: API_URL,
      data: deletedRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.filter((row) => row.id !== deletedRow.id),
        );
      },
      addNotification,
    });
  };

  const handleAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    await httpRequest({
      method: 'POST',
      url: API_URL,
      data: addFormData,
      onSuccess: (data) => {
        handleAddModalClose();
        setRows((prevRows) => [data.entry, ...prevRows]);
        handleAddClick();
        onAddSuccess();
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });
  };

  const handleUpload = async (file, onUploadSuccess, onUploadFailure) => {
    const formData = new FormData();
    formData.append('file', file);

    await httpRequest({
      method: 'POST',
      url: `${API_URL}upload/`,
      data: formData,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      onSuccess: (data) => {
        refreshData();
        onUploadSuccess(data);
      },
      onFailure: (data) => {
        onUploadFailure(data);
      },
      addNotification,
    });
  };

  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        setColumns(data.columns);
        setRows(data.rows);
        setRelatedData(data.relatedData);
      },
      onFailure: (data) => {
        setColumns([]);
        setRows([]);
        setRelatedData({});
      },
      addNotification,
    });

    setIsLoading(false);
  };

  const SubjectCard = ({ subject_row, subject_dict }) => {
    // const incomplete_data = subject_dict['subjectcomponent_list'].length === 0;
    const [expanded, setExpanded] = useState(false);  // incomplete_data
    const [selectedComponentEditRow, setSelectedComponentEditRow] = useState(null);
    const [isComponentEditModalOpen, setIsComponentEditModalOpen] = useState(false);
    const [isComponentAddModalOpen, setIsComponentAddModalOpen] = useState(false);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };

    const handleComponentAddClick = () => {
      setIsComponentAddModalOpen(true);
    };

    const handleComponentAddModalClose = () => {
      setIsComponentAddModalOpen(false);
    };

    const handleComponentEditClick = (row) => {
      console.log('Edit row ', row);
      setSelectedComponentEditRow(row);
    };

    useEffect(() => {
      if (selectedComponentEditRow !== null && selectedComponentEditRow !== undefined) {
        // Only open the modal after the row has been selected
        setIsComponentEditModalOpen(true);
      }
    }, [selectedComponentEditRow]); // Runs when selectedEditRow changes

    const handleComponentEditModalClose = () => {
      setIsComponentEditModalOpen(false);
      setSelectedComponentEditRow(null);
    };

    const handleComponentEditSave = async (updatedComponentEditRow, onEditFailure) => {
      const finalFormData = {
        ...updatedComponentEditRow,
        subject: subject_dict.id,
      };

      await httpRequest({
        method: 'PUT',
        url: SUB_SUBJECT_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === subject_dict.id ? {
                ...row,
                subjectcomponent_list: row.subjectcomponent_list.map((component) =>
                  component.id === selectedComponentEditRow.id ? data : component
                )
              } : row
            )
          );
          handleComponentEditModalClose();
        },
        onFailure: (data) => {
          onEditFailure(data);
        },
        addNotification,
      });
    };

    const handleComponentAddSave = async (addComponentFormData, onAddSuccess, onAddFailure) => {
      const finalFormData = {
        ...addComponentFormData,
        subject: subject_dict.id,
      };
        
      await httpRequest({
        method: 'POST',
        url: SUB_SUBJECT_API_URL,
        data: finalFormData,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === subject_dict.id
                ? {
                    ...row,
                    subjectcomponent_list: [
                      ...row.subjectcomponent_list,
                      data.entry
                    ],
                  }
                : row
            )
          );
          handleComponentAddModalClose();
          onAddSuccess();
        },
        onFailure: (data) => {
          onAddFailure(data);
        },
        addNotification,
      });
    };

    const handleComponentDelete = async (deletedComponentRow) => {
      await httpRequest({
        method: 'DELETE',
        url: SUB_SUBJECT_API_URL,
        data: deletedComponentRow,
        onSuccess: (data) => {
          setRows((prevRows) =>
            prevRows.map((row) =>
              row.id === subject_dict.id
                ? {
                    ...row,
                    subjectcomponent_list: row.subjectcomponent_list.filter(
                      (component) => component.id !== deletedComponentRow.id
                    ),
                  }
                : row
            )
          );
        },
        addNotification,
      });
    };

    return (
      <Box>
        <Card
          variant="outlined"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            transition: 'box-shadow 0.3s',
            '&:hover': {
              boxShadow: 3
            }
          }
        }>
          <CardHeader
            avatar={
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: subject_dict['color'],
                  borderRadius: '4px',  // '4px' -> Square, '50%' -> Round
                  display: 'inline-block',
                  // marginRight: 1,
                  border: '1px solid rgba(0,0,0,0.12)',
                }}
              />
            }
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5">{subject_dict['name']}</Typography>
              </Box>
            }
            action={
              <IconButton onClick={() => handleEditClick(subject_dict)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              px: 2,
              py: 0,
              alignItems: 'center',
              '& .MuiCardHeader-action': {
                alignSelf: 'center',
              },
            }}
            slotProps={{
              content: { sx: { py: 1.5 } },
            }}
          />
          <Divider/>
          
          <CardContent sx={{ pt: 0, pb: 1, flexGrow: 1, display: 'flex' }}>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 0.75, pt: 2, pb: 2 }}>
              <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Name:</Typography> {subject_dict['name'] ?? NA_LABEL}
              </Typography>
              {/* <Typography variant="body2">
                <Typography component="span" variant="body2" fontWeight="bold">Department:</Typography> {relatedData['department'][subject_dict['department']]['label'] ?? NA_LABEL}
              </Typography> */}
            </Box>

            {/* Expand Icon */}
            {/* <CardActions disableSpacing sx={{ p: 0}}>
              <IconButton
                size="small"
                onClick={handleExpandClick}
                sx={{ alignSelf: 'flex-start', mt: 1}}
              >
                <ExpandCircleDownOutlinedIcon
                  sx={{
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s'
                  }}
                />
              </IconButton>
            </CardActions> */}
          </CardContent>      

          {/* Collapsible Content */}
          <Collapse in={expanded}>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <CardContent sx={{ bgcolor: '#FBFBFB' }}>
              <Stack spacing={1}>
                {/* Add Subject Components Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" alignContent={'center'}>
                    <Typography component="span" variant="body2" fontWeight="bold" sx={{ mr: 1 }}>Subject Components</Typography>
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={handleComponentAddClick}
                    // sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}
                  >
                    Add more
                  </Button>
                </Box>

                {/* Subject Components Table */}
                {/* <CustomTable
                  columns={subjectComponentColumns}
                  rows={subject_dict['subjectcomponent_list']}
                  relatedData={relatedData}
                  onEdit={handleComponentEditClick}
                  showPagination={false}
                /> */}
              </Stack>
            </CardContent>
          </Collapse>
        </Card>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {/* <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              sx={{
                px: 4,
                display: { md: 'flex', xs: 'none' },
              }}
            >
              Upload
            </Button> */}
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    xl: 'repeat(2, 1fr)',
                    xxl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <SubjectCard
                      subject_row={index}
                      subject_dict={item_dict}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Add Subject Modal */}
      <Box>
        <AddModal
          open={isAddModalOpen}
          columns={columns}
          relatedData={relatedData}
          rows={rows}
          handleClose={handleAddModalClose}
          handleSave={handleAddSave}
          add_title="Add Subject"
        />
      </Box>

      {/* Upload Modal */}
      <UploadModal
        open={isUploadModalOpen}
        uploadTemplate={UPLOAD_TEMPLATE}
        handleClose={handleUploadModalClose}
        handleUpload={handleUpload}
      />

      {/* Edit Subject Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={columns}
          rowData={selectedEditRow}
          relatedData={relatedData}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Subject"
        />
      </Box>
    </Box>
  );
}
